<template>
  <div class="account-purchases">
    <b-row>
        <b-col
          :cols="12"
          :lg="2"
          :md="3">
          <vertical-navigation
            :links="links"
            title="Buying and Selling"/>
        </b-col>
        <b-col
          :cols="12"
          :lg="10"
          :md="9">
          <b-card
            v-if="!this.status && !this.role"
            class="mb-3">
            <h2 class="h5 mb-2">
              Viewing all items in Buying and Selling
            </h2>
            <p class="mb-2">
              You are viewing all items that you've created
              or received purchase requests for.
            </p>
            <p class="mb-0">
              Need help?
              <router-link
                to="/faq"
                target="_blank">
                <strong>
                  View our FAQ
                </strong>
              </router-link>
              to learn more about Buying and Selling.
              <span class="text-muted">
              (link opens in a new tab)
              </span>
            </p>
          </b-card>
            <b-card
              v-for="purchase in purchases"
              :key="purchase.id"
              class="mt-2 mb-4 p-0"
              no-body>
              <div class="account-purchases__purchase">
                <b-card-body>
                  <b-row>
                    <b-col
                      :cols="12"
                      :lg="3"
                      :md="2">
                      <div
                        class="account-purchases__image">
                        <router-link
                          class="text-dark"
                          :to="`/account/purchase/${purchase.id}`">
                          <listing-image
                            class="mb-2"
                            :src="purchase.imageUrl"/>
                        </router-link>
                      </div>
                    </b-col>
                    <b-col
                      :cols="12"
                      :lg="9"
                      :md="10">
                    <div class="account-purchases__purchase-name">
                      <router-link
                        class="text-dark"
                        :to="`/account/purchase/${purchase.id}`">
                        <h2 class="h4 mb-3">
                          {{ purchase.title }}
                        </h2>
                      </router-link>
                      <!-- Start purchase confirmed (buyer, seller) -->
                      <div
                        v-if="purchase.status === 'Confirmed'">
                        <p v-if="purchase.isSeller">
                          You have confirmed this purchase request.
                          Coordinate with the buyer using ZoopLocal Messages
                          to complete the sale of your item.
                        </p>
                        <p v-else>
                          The seller has confirmed your purchase request.
                          Coordinate with the seller using ZoopLocal Messages
                          to complete the purchase of this item.
                        </p>
                        <p>
                          <font-awesome-icon
                            :icon="['far', 'clock']"/>
                          <span class="font-weight-bold">
                            You have until {{ purchase.expiresAt | date }}
                            to complete this purchase.
                          </span>
                        </p>

                        <!-- Last message excerpt -->
                        <div
                          v-if="purchase.lastMessage">
                          <hr/>
                          <div>
                            <h3
                              class="h5 mb-3 d-inline-block">
                              Messaging
                            </h3>
                            <span
                              v-if="purchase.lastMessage && purchase.lastMessage.isUnread"
                              class="ml-2 mb-3">
                              <b-badge
                                size="sm"
                                variant="warning"
                                pill>
                                UNREAD
                              </b-badge>
                            </span>
                          </div>
                          <p
                            v-if="purchase.lastMessage"
                            class="text-muted mb-0">
                            <span v-if="purchase.lastMessage.isSender">
                            </span>
                            <span v-else>
                            </span>
                          </p>
                          <!-- eslint-disable max-len -->
                          <router-link
                            :to="`/account/purchase/${purchase.id}`">
                            <div
                              class="account-purchases__message-excerpt mb-1"
                              :class="{ sent: purchase.lastMessage.isSender, received: !purchase.lastMessage.isSender }">
                              {{ purchase.lastMessage.message }}
                              <!-- eslint-enable max-len -->
                              <div class="account-purchases__message-excerpt-meta">
                                <span v-if="purchase.lastMessage.isSender">
                                  Sent by you
                                  {{ purchase.lastMessage.createdAt | relativeDate }}
                                </span>
                                <span v-else>
                                  Sent by
                                  <span v-if="purchase.isSeller">
                                    buyer
                                  </span>
                                  <span v-else>
                                    seller
                                  </span>
                                  {{ purchase.lastMessage.createdAt | relativeDate }}
                                </span>
                              </div>
                            </div>
                          </router-link>
                        </div>
                        <!-- End last message -->
                        <div v-else>
                          <h3 class="h5 mb-1">
                            Messaging
                          </h3>
                          <router-link
                            :to="`/account/purchase/${purchase.id}`">
                            <strong>
                              Send the first message &#8250;
                            </strong>
                          </router-link>
                        </div>
                      </div>

                      <!-- End purchase confirmed (buyer, seller) -->
                      <!-- Start purchase pending (buyer, seller) -->
                      <div
                        v-else-if="purchase.status === 'Pending'">
                        <p v-if="purchase.isSeller">
                          You have received a purchase request for this item.
                          Once you approve this request, you will be able to
                          connect with the buyer using ZoopLocal Messages.
                        </p>
                        <p v-else>
                          You have sent a purchase request for this item.
                          The seller is reviewing your request.
                        </p>
                        <div>
                        </div>
                        <div v-if="purchase.isSeller">
                          <hr>
                          <div>
                            <p>
                              <b-badge
                                size="sm"
                                variant="warning"
                                pill>
                                ACTION REQUIRED
                              </b-badge>
                            </p>
                            <p>
                              <font-awesome-icon
                                size="sm"
                                :icon="['far', 'clock']"/>
                              You have until {{ purchase.expiresAt | date }}
                              to approve this purchase request.``
                            </p>
                          </div>
                        </div>
                        <div v-else>
                          <p>
                            <font-awesome-icon
                              size="xs"
                              :icon="['far', 'clock']"/>
                            The seller has until {{ purchase.expiresAt | date }}
                            to approve this purchase request.
                          </p>
                        </div>
                      </div>
                      <!-- End purchase pending (buyer, seller) -->
                      <!-- Start purchase expired (buyer, seller) -->
                      <div v-else-if="purchase.status === 'Expired'">
                        <p>
                          This Purchase Request is no longer available.
                        </p>
                        <div class="text-danger">
                          <font-awesome-icon
                            :icon="['fas', 'times']"/>
                          <strong> Expired {{ purchase.expiresAt | relativeDate }}</strong>
                        </div>
                      </div>
                      <!-- End purchase expired (buyer, seller) -->
                      <!-- Start purchase completed (buyer, seller) -->
                      <div v-else-if="purchase.status === 'Completed'">
                        <p>
                          This Purchase Request has been completed.
                          You cannot send additional messages, but you can view
                          past messages you exchanged with the
                          <span v-if="purchase.isSeller">
                            buyer.
                          </span>
                          <span v-else>
                            seller.
                          </span>
                        </p>
                        <div class="text-success">
                          <font-awesome-icon
                            :icon="['fas', 'check']"/>
                          <strong> Completed {{ purchase.updatedAt | relativeDate }}</strong>
                        </div>
                      </div>
                      <!-- End purchase expired (buyer, seller) -->
                    </div>
                  </b-col>
                </b-row>
                </b-card-body>
              <b-card-footer
                class="text-right">
                <span v-if="purchase.status === 'Confirmed'">
                  <router-link
                    :to="`/account/purchase/${purchase.id}`">
                    <strong>
                      Continue the conversation &#8250;
                    </strong>
                  </router-link>
                </span>
                <span v-if="purchase.status === 'Expired'">
                  <router-link
                    :to="`/account/purchase/${purchase.id}`">
                    <strong>
                      View Message History
                    </strong>
                  </router-link>
                </span>
                <span v-if="purchase.status === 'Pending' && purchase.isSeller">
                  <a
                    href="javascript:void(0);"
                    class="mr-3 text-success font-weight-bold"
                    @click="confirmPurchaseRequest(purchase.id)">
                    Confirm Request&hellip;
                  </a>
                  <a
                    href="javascript:void(0);"
                    class="mr-3 text-danger font-weight-bold"
                    @click="denyPurchaseRequest(purchase.id)">
                    Deny Request&hellip;
                  </a>
                </span>
              </b-card-footer>
            </div>
            </b-card>
            <div
              v-if="purchases.length === 0"
              class="account-listings__pagination text-center p-3">
              <h3 class="h5">
                There are no items in this category.
              </h3>
              <p class="mb-0">
                Go to the
                <router-link
                  to="/account/purchases">
                  All Items page
                </router-link>
                to see all items you've
                sent or received Purchase Requests for.
              </p>
              <p
                v-if="role && role === 'selling'"
                class="mt-2 mb-0">
                If you're looking listings you've created, you can find them
                on the
                <router-link
                  to="/account/listings">
                  Listings page.
                </router-link>
              </p>
            </div>
        </b-col>
    </b-row>
  </div>
</template>

<script>
import relativeDate from '@/filters/relativeDate.filter'
import date from '@/filters/date.filter'
import VerticalNavigation from '@/components/navigation/VerticalNavigation.vue'
import ListingImage from '@/components/images/ListingImage.vue'

export default {
  name: 'AccountPurchases',
  components: {
    ListingImage,
    VerticalNavigation,
  },
  filters: {
    relativeDate,
    date,
  },
  data() {
    return {
      purchases: [],
      purchasesInterval: null,
      links: [
        {
          type: 'subheading',
          name: 'All Items',
        },
        {
          type: 'link',
          name: 'View All',
          to: '/account/purchases',
        },
        {
          type: 'subheading',
          name: 'Buying',
        },
        {
          type: 'link',
          name: 'Confirmed',
          to: '/account/purchases/buying/confirmed',
        },
        {
          type: 'link',
          name: 'Pending',
          to: '/account/purchases/buying/pending',
        },
        {
          type: 'link',
          name: 'Completed',
          to: '/account/purchases/buying/completed',
        },
        {
          type: 'link',
          name: 'Expired',
          to: '/account/purchases/buying/expired',
        },
        {
          type: 'subheading',
          name: 'Selling',
        },
        {
          type: 'link',
          name: 'Confirmed',
          to: '/account/purchases/selling/confirmed',
        },
        {
          type: 'link',
          name: 'Pending',
          to: '/account/purchases/selling/pending',
        },
        {
          type: 'link',
          name: 'Completed',
          to: '/account/purchases/selling/completed',
        },
        {
          type: 'link',
          name: 'Expired',
          to: '/account/purchases/selling/expired',
        },
      ],
    }
  },
  computed: {
    status() {
      return this.$route.params.status
    },
    role() {
      return this.$route.params.role
    },
  },
  methods: {
    async confirmPurchaseRequest(id) {
      console.log(id)
      const answer = await this.$bvModal.msgBoxConfirm('Are you sure you want to confirm this request?', {
        title: 'Confirm Request',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'success',
        okTitle: 'Yes, confirm this request',
        cancelTitle: 'No, cancel',
        cancelVariant: 'default',
        footerClass: 'p-2',
        hideHeaderClose: true,
        centered: true,
      })
      if (answer === false) return
      try {
        await this.$http.post(`/request/${id}/confirm`)
      } catch (err) {
        console.log(err)
      }
      this.$router.push(`/account/purchase/${id}`)
    },
    async denyPurchaseRequest(id) {
      const answer = await this.$bvModal.msgBoxConfirm('Are you sure you want to deny this request?', {
        title: 'Deny Request',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes, deny this request',
        cancelTitle: 'No, cancel',
        cancelVariant: 'default',
        footerClass: 'p-2',
        hideHeaderClose: true,
        centered: true,
      })
      if (answer === false) return
      try {
        await this.$http.post(`/request/${id}/deny`)
      } catch (err) {
        console.log(err)
      }
      this.fetchPurchases()
    },
    async fetchPurchases() {
      this.isPurchasesLoading = true
      try {
        const { data: { requests } } = await this.$http.get('/requests', {
          params: {
            role: this.role,
            status: this.status,
          },
        })
        this.purchases = requests
      } catch (err) {
        console.log(err)
      }
      this.isPurchasesLoading = false
    },
  },
  async created() {
    this.fetchPurchases()

    this.purchasesInterval = setInterval(() => {
      this.fetchPurchases()
    }, 10000)
  },
  beforeRouteLeave(to, from, next) {
    clearInterval(this.purchasesInterval)
    next()
  },
  watch: {
    '$route.params.role': function updateOnRole() {
      this.fetchPurchases()
    },
    '$route.params.status': function updateOnStatus() {
      this.fetchPurchases()
    },
  },
}
</script>

<style lang="scss" scoped>
.account-purchases__message-excerpt {
  padding: 10px;

  &.received {
    background-color: #f7f7f7;
    color: #000000;
  }

  &.sent {
    // background-color: #1a6199;
    // color: #ffffff;
    background-color: #f7f7f7;
    color: #000000;
  }
}

.account-purchases__message-excerpt-meta {
  font-size: 0.8rem;
  margin: 0;
  opacity: 0.8;
  padding: 0;
}

.account-purchases__image {
  width: 100%;
}

.account-purchases__purchase-footer {
  align-items: center;
  display: flex;
  justify-content: space-between;
}
</style>
